import { useEffect, useMemo, useState } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Trash2, Inbox, Clock, Wrench, CheckCircle2, XCircle, ImageOff, MessageSquare, Save, Check } from "lucide-react";

// Status configuration. Keys match backend status codes.
const STATUSES = {
    pending: {
        label: "Oczekujące",
        icon: Clock,
        badge: "bg-sky-400/15 text-sky-300 border-sky-400/30",
        dot: "bg-sky-400",
    },
    in_progress: {
        label: "W trakcie",
        icon: Wrench,
        badge: "bg-amber-400/15 text-amber-300 border-amber-400/30",
        dot: "bg-amber-400",
    },
    done: {
        label: "Wykonane",
        icon: CheckCircle2,
        badge: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
        dot: "bg-emerald-400",
    },
    cancelled: {
        label: "Anulowane",
        icon: XCircle,
        badge: "bg-red-400/15 text-red-300 border-red-400/30",
        dot: "bg-red-400",
    },
};

const STATUS_ORDER = ["pending", "in_progress", "done", "cancelled"];

const normalizeStatus = (s) => (STATUSES[s] ? s : "pending");

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("pl-PL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function ReportsManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [filter, setFilter] = useState("all");
    const [comments, setComments] = useState({}); // { [id]: draftText }
    const [savingComment, setSavingComment] = useState(null);
    const [savedComment, setSavedComment] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bug-reports");
            const list = Array.isArray(data) ? data : [];
            setItems(list);
            setComments(
                list.reduce((acc, it) => {
                    acc[it.id] = it.admin_comment || "";
                    return acc;
                }, {})
            );
            setError("");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const saveComment = async (id) => {
        setSavingComment(id);
        setError("");
        const text = comments[id] ?? "";
        try {
            await api.put(`/bug-reports/${id}`, { admin_comment: text });
            setItems((prev) =>
                prev.map((it) => (it.id === id ? { ...it, admin_comment: text } : it))
            );
            setSavedComment(id);
            setTimeout(() => setSavedComment((cur) => (cur === id ? null : cur)), 2000);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSavingComment(null);
        }
    };

    const counts = useMemo(() => {
        const c = { all: items.length, pending: 0, in_progress: 0, done: 0, cancelled: 0 };
        items.forEach((it) => {
            c[normalizeStatus(it.status)] += 1;
        });
        return c;
    }, [items]);

    const visible = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((it) => normalizeStatus(it.status) === filter);
    }, [items, filter]);

    const changeStatus = async (id, status) => {
        setUpdatingId(id);
        // optimistic update
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
        try {
            await api.put(`/bug-reports/${id}`, { status });
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            await load();
        } finally {
            setUpdatingId(null);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć to zgłoszenie? Tej operacji nie można cofnąć.")) return;
        try {
            await api.delete(`/bug-reports/${id}`);
            setItems((prev) => prev.filter((it) => it.id !== id));
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div data-testid="admin-reports">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="label-eyebrow">Zgłoszenia ({items.length})</div>
                <div className="flex flex-wrap gap-2">
                    <FilterPill
                        active={filter === "all"}
                        onClick={() => setFilter("all")}
                        label="Wszystkie"
                        count={counts.all}
                    />
                    {STATUS_ORDER.map((s) => (
                        <FilterPill
                            key={s}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            label={STATUSES[s].label}
                            count={counts[s]}
                            dot={STATUSES[s].dot}
                        />
                    ))}
                </div>
            </div>

            {error && (
                <div data-testid="reports-error" className="text-sm text-red-400 mb-4">
                    {error}
                </div>
            )}

            {loading ? (
                <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
            ) : visible.length === 0 ? (
                <div className="panel-yellow p-10 text-center text-sm text-[color:var(--text-secondary)]">
                    <Inbox size={26} className="mx-auto mb-3 text-[color:var(--primary)]" />
                    {items.length === 0
                        ? "Brak zgłoszeń. Gdy ktoś wyśle zgłoszenie, pojawi się tutaj."
                        : "Brak zgłoszeń w tym statusie."}
                </div>
            ) : (
                <ul className="space-y-4">
                    {visible.map((r) => {
                        const status = normalizeStatus(r.status);
                        const cfg = STATUSES[status];
                        const StatusIcon = cfg.icon;
                        const imgs = (Array.isArray(r.image_urls) && r.image_urls.length > 0
                            ? r.image_urls
                            : r.image_url
                              ? [r.image_url]
                              : []);
                        return (
                            <li
                                key={r.id}
                                data-testid={`report-row-${r.id}`}
                                className="panel-yellow p-5 flex flex-col md:flex-row gap-5"
                            >
                                {/* Images */}
                                <div className="shrink-0 w-full md:w-36">
                                    {imgs.length > 0 ? (
                                        <div className={`grid gap-1.5 ${imgs.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                                            {imgs.map((u, idx) => (
                                                <a
                                                    key={idx}
                                                    href={resolveMediaUrl(u)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={`block rounded-md overflow-hidden border border-white/10 bg-[color:var(--bg-base)] ${imgs.length === 1 ? "h-32" : "aspect-square"}`}
                                                >
                                                    <img
                                                        src={resolveMediaUrl(u)}
                                                        alt={`${r.title} ${idx + 1}`}
                                                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="w-full h-32 rounded-md border border-dashed border-white/10 bg-[color:var(--bg-base)] flex items-center justify-center text-[color:var(--text-muted)]">
                                            <ImageOff size={22} />
                                        </div>
                                    )}
                                    {imgs.length > 1 && (
                                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-1.5 text-center">
                                            {imgs.length} zdjęć
                                        </div>
                                    )}
                                </div>

                                {/* Body */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                                        {r.ticket && (
                                            <span
                                                data-testid={`report-ticket-${r.id}`}
                                                className="font-mono text-xs tracking-wider text-[color:var(--primary)] bg-[color:var(--primary)]/10 border border-[color:var(--primary)]/30 rounded px-2 py-0.5"
                                            >
                                                {r.ticket}
                                            </span>
                                        )}
                                        <span
                                            data-testid={`report-status-badge-${r.id}`}
                                            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${cfg.badge}`}
                                        >
                                            <StatusIcon size={12} /> {cfg.label}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                            {formatDate(r.created_at)}
                                        </span>
                                    </div>
                                    <h3 className="text-white font-medium break-words">{r.title}</h3>
                                    <p className="text-xs text-[color:var(--text-secondary)] mt-1 whitespace-pre-wrap break-words">
                                        {r.description}
                                    </p>

                                    {/* Status actions */}
                                    <div className="flex flex-wrap items-center gap-2 mt-4">
                                        <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mr-1">
                                            Status:
                                        </span>
                                        {STATUS_ORDER.map((s) => {
                                            const active = status === s;
                                            const sc = STATUSES[s];
                                            const SIcon = sc.icon;
                                            return (
                                                <button
                                                    key={s}
                                                    data-testid={`report-set-${s}-${r.id}`}
                                                    disabled={active || updatingId === r.id}
                                                    onClick={() => changeStatus(r.id, s)}
                                                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border transition-colors disabled:cursor-default ${
                                                        active
                                                            ? sc.badge
                                                            : "border-white/10 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30"
                                                    }`}
                                                >
                                                    {updatingId === r.id && !active ? (
                                                        <Loader2 size={12} className="animate-spin" />
                                                    ) : (
                                                        <SIcon size={12} />
                                                    )}
                                                    {sc.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Admin comment (visible to reporter) */}
                                    <div className="mt-4">
                                        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-1.5">
                                            <MessageSquare size={12} /> Komentarz dla zgłaszającego
                                        </label>
                                        <textarea
                                            data-testid={`report-comment-input-${r.id}`}
                                            value={comments[r.id] ?? ""}
                                            onChange={(e) =>
                                                setComments((c) => ({ ...c, [r.id]: e.target.value }))
                                            }
                                            rows={2}
                                            placeholder="Napisz odpowiedź / komentarz, który zobaczy gracz po wpisaniu numeru zgłoszenia…"
                                            className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[color:var(--primary)] resize-y"
                                        />
                                        <div className="flex items-center gap-3 mt-2">
                                            <button
                                                type="button"
                                                data-testid={`report-comment-save-${r.id}`}
                                                disabled={
                                                    savingComment === r.id ||
                                                    (comments[r.id] ?? "") === (r.admin_comment || "")
                                                }
                                                onClick={() => saveComment(r.id)}
                                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border border-[color:var(--primary)]/40 text-[color:var(--primary)] hover:bg-[color:var(--primary)]/10 disabled:opacity-50 disabled:cursor-default"
                                            >
                                                {savingComment === r.id ? (
                                                    <Loader2 size={12} className="animate-spin" />
                                                ) : savedComment === r.id ? (
                                                    <Check size={12} />
                                                ) : (
                                                    <Save size={12} />
                                                )}
                                                {savedComment === r.id ? "Zapisano" : "Zapisz komentarz"}
                                            </button>
                                            {r.admin_comment && (comments[r.id] ?? "") === r.admin_comment && (
                                                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                                    Komentarz widoczny dla gracza
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Delete */}
                                <div className="shrink-0 flex md:flex-col items-end">
                                    <button
                                        data-testid={`report-delete-${r.id}`}
                                        onClick={() => onDelete(r.id)}
                                        className="text-red-400 hover:text-red-300 p-2"
                                        aria-label="Usuń zgłoszenie"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function FilterPill({ active, onClick, label, count, dot }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${
                active
                    ? "border-[color:var(--primary)] text-[color:var(--primary)]"
                    : "border-white/10 text-[color:var(--text-secondary)] hover:text-white"
            }`}
        >
            {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
            {label}
            <span className="text-[color:var(--text-muted)]">{count}</span>
        </button>
    );
}
